import PropTypes from 'prop-types';
import FancyTimeStamp from '../../../../FancyTimeStamp';
import './SystemMessageSeparator.css';
import logger from '../../../../../utils/logger';
import { GEMATIK_ROOM } from '../../../../../constants';
import { useI18n } from '../../../../../context/I18nContext';
import { useMatrixClient } from '../../../../../context/MatrixContext';

const SHOW_NAME_CHANGE = false;
const SHOW_TOPIC_CHANGE = false;
const SHOW_JOIN_RULES_CHANGE = false;
const SHOW_VISIBILITY_CHANGE = false;
const SHOW_POWER_LEVEL_CHANGE = false;
const SHOW_PINNED_EVENTS_CHANGE = false;

const SystemMessageSeparator = ({ unixTime, msg }) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const myUserId = client?.getUserId();

  if (typeof unixTime !== 'number' || Number.isNaN(unixTime)) {
    return null;
  }
  const type = msg?.getType ? msg.getType() : 'unknown';

  const newDisplayName = msg.getContent?.()?.displayname || 'Someone';
  const targetUserId = msg.getStateKey?.() || 'Someone';
  const sender = msg.getSender?.() || msg.getSender;
  const senderDisplayName = msg.sender?.name || sender || 'Someone';
  let label;
  let extraClass = '';
  switch (type) {
    case 'm.room.create':
      label = (
        <>
          Conversation created on <FancyTimeStamp unixTime={unixTime} briefDate />
        </>
      );
      extraClass = 'system-message-separator--room-create';
      break;
    case 'm.room.name':
    case GEMATIK_ROOM.ROOM_NAME: {
      if (!SHOW_NAME_CHANGE) {
        return null;
      }
      const newName = msg.getContent()?.name;
      const prevName = msg.getPrevContent?.()?.name;
      if (newName) {
        label = `${senderDisplayName} changed the room name ${prevName ? `from ${prevName}` : ''} to ${newName}`;
      } else {
        label = `${senderDisplayName} removed the room name`;
      }
      break;
    }
    case 'm.room.topic': 
    case GEMATIK_ROOM.ROOM_TOPIC: {
      if (!SHOW_TOPIC_CHANGE) {
        return null;
      }
      const newTopic = msg.getContent()?.topic;
      const prevTopic = msg.getPrevContent?.()?.topic;
      if (newTopic) {
        label = `${senderDisplayName} changed the room topic ${prevTopic ? `from ${prevTopic}` : ''} to ${newTopic}`;
      } else {
        label = `${senderDisplayName} removed the room topic`;
      }
      break;
    }
    case 'm.room.avatar': {
      const prevAvatarUrl = msg.getPrevContent?.()?.url;
      const newAvatarUrl = msg.getContent?.()?.url;

      if (!prevAvatarUrl && newAvatarUrl) {
        label = `${senderDisplayName} set the room's avatar`;
      } else if (prevAvatarUrl && newAvatarUrl) {
        label = `${senderDisplayName} changed the room's avatar`;
      } else if (prevAvatarUrl && !newAvatarUrl) {
        label = `${senderDisplayName} removed the room's avatar`;
      }
      break;
    }
    
    case 'm.room.member': {
      const content = msg.getContent?.() || {};
      const prevContent = msg.getPrevContent?.() || {};
      const membership = content.membership;
      const prevMembership = prevContent.membership;
      const avatarChanged =
        membership === 'join' &&
        prevMembership === 'join' &&
        content.avatar_url !== prevContent.avatar_url &&
        (content.avatar_url || prevContent.avatar_url);

      if (avatarChanged) {
        const isCurrentUser = targetUserId === myUserId;
        const profileChangeText = isCurrentUser
          ? t('youChangedYourProfilePicture')
          : t('changedTheirProfilePicture');
        const displayName = isCurrentUser ? t('you') : newDisplayName;

        label = `${displayName} ${profileChangeText}`;
      } else if (membership === 'join' && prevMembership !== 'join') {
        const isCurrentUser = targetUserId === myUserId;
        label = isCurrentUser ? t('youJoinedRoom') : `${newDisplayName} joined the room`;
      } else if (membership === 'leave' && prevMembership === 'join') {
        const isCurrentUser = targetUserId === myUserId;
        label = isCurrentUser ? t('youLeftRoom') : `${newDisplayName} left the room`;
      } else if (membership === 'leave' && prevMembership === 'invite') {
        // Check if user declined or if invite was rescinded
        // If sender === targetUserId, the user declined the invite themselves
        // If sender !== targetUserId, someone else (the inviter or admin) rescinded it
        const isCurrentUser = targetUserId === myUserId;
        const userDeclined = sender === targetUserId;

        logger.debug('Processing invite decline/rescind', {
          membership,
          prevMembership,
          sender,
          targetUserId,
          userDeclined,
          isCurrentUser,
        });

        if (userDeclined) {
          label = isCurrentUser ? t('youDeclinedInvitation') : `${newDisplayName} declined the invitation`;
        } else {
          label = isCurrentUser
            ? t('invitationRescindedForYou', { sender: senderDisplayName })
            : t('invitationRescindedForUser', { sender: senderDisplayName, user: newDisplayName });
        }
      } else if (membership === 'invite') {
        const isCurrentUser = targetUserId === myUserId;
        label = isCurrentUser
          ? t('youWereInvited', { sender: senderDisplayName })
          : t('userWasInvited', { sender: senderDisplayName, user: newDisplayName });
      } else if (membership === 'ban') {
        label = `${newDisplayName} was banned`;
      } else {
        logger.debug('Unhandled m.room.member membership change:', { content: msg.getContent() });
        return null;
      }
      break;
    }
    case 'm.room.join_rules': {
      if (!SHOW_JOIN_RULES_CHANGE) {
        return null;
      }
      const newRule = msg.getContent()?.join_rule;
      if (newRule === 'public') {
        label = `${senderDisplayName} made this room public`;
      } else if (newRule === 'invite') {
        label = `${senderDisplayName} made this room invite-only`;
      } else if (newRule === 'knock') {
        label = `${senderDisplayName} changed this room to allow knocking`;
      } else if (newRule === 'restricted') {
        label = `${senderDisplayName} made this room restricted`;
      } else {
        label = `${senderDisplayName} changed the room access rules`;
      }
      break;
    }
    case 'm.room.history_visibility': {
      if (!SHOW_VISIBILITY_CHANGE) {
        return null;
      }
      const visibility = msg.getContent()?.history_visibility;
      if (visibility === 'world_readable') {
        label = `${senderDisplayName} made future room history visible to anyone`;
      } else if (visibility === 'shared') {
        label = `${senderDisplayName} made future room history visible to all room members`;
      } else if (visibility === 'invited') {
        label = `${senderDisplayName} made future room history visible to all room members from the point they are invited`;
      } else if (visibility === 'joined') {
        label = `${senderDisplayName} made future room history visible to all room members from the point they joined`;
      } else {
        label = `${senderDisplayName} changed room history visibility`;
      }
      break;
    }
    case 'm.room.power_levels': {
      if (!SHOW_POWER_LEVEL_CHANGE) {
        return null;
      }
      const content = msg.getContent() || {};
      const prevContent = msg.getPrevContent?.() || {};
      const users = content.users || {};
      const prevUsers = prevContent.users || {};

      // Find users whose power levels changed
      const changedUsers = [];
      Object.keys({ ...users, ...prevUsers }).forEach(userId => {
        const newLevel = users[userId];
        const oldLevel = prevUsers[userId];
        if (newLevel !== oldLevel && newLevel !== undefined) {
          changedUsers.push({ userId, oldLevel: oldLevel || 0, newLevel });
        }
      });

      if (changedUsers.length === 1) {
        const change = changedUsers[0];
        const userId = change.userId.split(':')[0].substring(1); // Extract username from @user:server
        if (change.newLevel === 100) {
          label = `${senderDisplayName} made ${userId} an admin`;
        } else if (change.newLevel === 50) {
          label = `${senderDisplayName} made ${userId} a moderator`;
        } else if (change.newLevel === 0 && change.oldLevel > 0) {
          label = `${senderDisplayName} revoked ${userId}'s moderator status`;
        } else {
          label = `${senderDisplayName} changed ${userId}'s power level from ${change.oldLevel} to ${change.newLevel}`;
        }
      } else if (changedUsers.length > 1) {
        label = `${senderDisplayName} changed the power levels of ${changedUsers.length} users`;
      } else {
        label = `${senderDisplayName} changed the room permissions`;
      }
      break;
    }
    case 'm.room.pinned_events': {
      if (!SHOW_PINNED_EVENTS_CHANGE) {
        return null;
      }
      const pinnedEvents = msg.getContent()?.pinned || [];
      const prevPinnedEvents = msg.getPrevContent?.()?.pinned || [];

      // Check "unpinned all" first before comparing lengths
      if (pinnedEvents.length === 0 && prevPinnedEvents.length > 0) {
        label = `${senderDisplayName} unpinned all messages`;
      } else if (pinnedEvents.length > prevPinnedEvents.length) {
        label = `${senderDisplayName} pinned a message`;
      } else if (pinnedEvents.length < prevPinnedEvents.length) {
        label = `${senderDisplayName} unpinned a message`;
      } else {
        label = `${senderDisplayName} changed pinned messages`;
      }
      break;
    }
    case 'm.room.tombstone': {
      const replacementRoom = msg.getContent()?.replacement_room;
      if (replacementRoom) {
        label = `${senderDisplayName} upgraded this room. The conversation continues in a new room.`;
      } else {
        label = `${senderDisplayName} closed this room`;
      }
      break;
    }
    default:
      logger.debug(`Unhandled system message type: ${type}`, { msg });
      return null;
  }

  return (
    <div className={`system-message-separator${extraClass ? ' ' + extraClass : ''}`}>
      <span className="system-message-separator-label">{label}</span>
    </div>
  );
};

SystemMessageSeparator.propTypes = {
  unixTime: PropTypes.number.isRequired,
  msg: PropTypes.object.isRequired,
};

export default SystemMessageSeparator;
